/**
 * FERP Portal - Task Templates Page
 *
 * Tabulator table showing built-in + saved task templates.
 * Users can:
 * - Browse all templates in a grid (name, category, status, domain, tags)
 * - Clone a template into a new task via ComprehensiveTaskForm
 * - Create a new blank task
 * - View/edit a template as expandable JSON with inline editing
 * - Delete saved templates
 * - Publish draft templates (architect only)
 *
 * Uses shared tabulatorConfig + tabulator-mantine.css for consistent theming.
 * Admin/Architect only — regular users don't access this page.
 *
 * Backend: DynamoDB (primary) + Valkey (cache) via useTaskTemplates hook.
 */

import { Tabulator, type ColumnDefinition } from '@/types/tabulator';
import 'tabulator-tables/dist/css/tabulator.min.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Code,
  Container,
  CopyButton,
  Group,
  JsonInput,
  Loader,
  Modal,
  ScrollArea,
  SegmentedControl,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconCheck,
  IconCopy,
  IconList,
  IconPlus,
  IconTemplate,
} from '@tabler/icons-react';
import '@/styles/tabulator-mantine.css';

import {
  ComprehensiveTaskForm,
  serializeForInitialData,
  TaskGroupBrowser,
  useTaskTemplates,
  type TaskFormState,
  type TaskTemplateView,
} from '@ferp/workflow-forms';

import { useHasRole } from '@/auth';

import {
  TABULATOR_DEFAULTS,
  textColumn,
  typeBadgeColumn,
  priorityColumn,
  actionsColumn,
  timestampColumn,
} from '@/utils/tabulatorConfig';

// ============================================================================
// JSON <-> structured text helpers (same converter as TaskFormToolbar)
// ============================================================================

function toStructuredText(value: unknown, indent = 0): string {
  const pad = '  '.repeat(indent);
  if (value === null || value === undefined) return `${pad}null`;
  if (typeof value === 'boolean') return `${pad}${value}`;
  if (typeof value === 'number') return `${pad}${value}`;
  if (typeof value === 'string') {
    if (value.includes('\n')) {
      return `${pad}|\n${value.split('\n').map(l => `${pad}  ${l}`).join('\n')}`;
    }
    if (/^(true|false|null|[-\d])/.test(value) || value.includes(':') || value.includes('#')) {
      return `${pad}"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
    }
    return `${pad}${value}`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return `${pad}[]`;
    return value.map(item => {
      if (typeof item === 'object' && item !== null) return `${pad}- ${toStructuredText(item, indent + 1).trimStart()}`;
      return `${pad}- ${toStructuredText(item, 0).trimStart()}`;
    }).join('\n');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return `${pad}{}`;
    return entries.map(([k, v]) => {
      if (v === null || v === undefined) return `${pad}${k}: null`;
      if (typeof v === 'object') return `${pad}${k}:\n${toStructuredText(v, indent + 1)}`;
      return `${pad}${k}: ${toStructuredText(v, 0).trimStart()}`;
    }).join('\n');
  }
  return `${pad}${String(value)}`;
}

// ============================================================================
// Unified row type for the table
// ============================================================================

interface TemplateRow {
  id: string;
  name: string;
  category: string;
  domain: string;
  tags: string;
  type: 'built-in' | 'saved';
  status: string;
  priority: string;
  savedAt: string;
  _source: TaskTemplateView;
}

function templateToRow(t: TaskTemplateView): TemplateRow {
  return {
    id: t.id,
    name: t.name,
    category: t.category || '-',
    domain: t.domain || '-',
    tags: t.tags.join(', '),
    type: t.isBuiltin ? 'built-in' : 'saved',
    status: t.status,
    priority: t.formState?.priority as string || 'normal',
    savedAt: t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '-',
    _source: t,
  };
}

// ============================================================================
// JSON Tree Viewer (collapsible)
// ============================================================================

interface JsonNodeProps {
  keyName: string;
  value: unknown;
  depth: number;
  path: string[];
}

function JsonNode({ keyName, value, depth, path }: JsonNodeProps) {
  const [expanded, setExpanded] = useState(depth < 2);

  if (value === null || value === undefined) {
    return (
      <Group gap={4} style={{ paddingLeft: depth * 16 }}>
        <Text size="xs" c="dimmed" ff="monospace">{keyName}:</Text>
        <Text size="xs" c="gray" ff="monospace" fs="italic">null</Text>
      </Group>
    );
  }

  if (typeof value === 'object') {
    const isArray = Array.isArray(value);
    const entries = isArray ? (value as unknown[]).map((v, i) => [String(i), v] as const) : Object.entries(value as Record<string, unknown>);
    const count = entries.length;

    return (
      <Stack gap={0}>
        <Group
          gap={4}
          style={{ paddingLeft: depth * 16, cursor: 'pointer' }}
          onClick={() => setExpanded(e => !e)}
        >
          <Text size="xs" c="blue" ff="monospace" fw={600}>
            {expanded ? '\u25BC' : '\u25B6'}
          </Text>
          <Text size="xs" ff="monospace" fw={500}>{keyName}</Text>
          <Text size="xs" c="dimmed" ff="monospace">
            {isArray ? `[${count}]` : `{${count}}`}
          </Text>
        </Group>
        {expanded && entries.map(([k, v]) => (
          <JsonNode key={k} keyName={k} value={v} depth={depth + 1} path={[...path, k]} />
        ))}
      </Stack>
    );
  }

  const color = typeof value === 'string' ? 'green' : typeof value === 'number' ? 'orange' : typeof value === 'boolean' ? 'violet' : 'gray';
  const display = typeof value === 'string' ? `"${value.length > 80 ? value.slice(0, 80) + '...' : value}"` : String(value);

  return (
    <Group gap={4} style={{ paddingLeft: depth * 16 }} wrap="nowrap">
      <Text size="xs" c="dimmed" ff="monospace">{keyName}:</Text>
      <Text size="xs" c={color} ff="monospace" style={{ wordBreak: 'break-all' }}>{display}</Text>
    </Group>
  );
}

function JsonTreeView({ data }: { data: Record<string, unknown> }) {
  return (
    <ScrollArea h="calc(100vh - 200px)">
      <Stack gap={0} py="xs">
        {Object.entries(data).map(([k, v]) => (
          <JsonNode key={k} keyName={k} value={v} depth={0} path={[k]} />
        ))}
      </Stack>
    </ScrollArea>
  );
}

// ============================================================================
// Main Page Component
// ============================================================================

export function TaskTemplatesPage() {
  const tableRef = useRef<HTMLDivElement>(null);
  const tabulatorRef = useRef<any | null>(null);

  const [view, setView] = useState<'table' | 'form'>('table');
  const [formKey, setFormKey] = useState(0);
  const [initialData, setInitialData] = useState<Record<string, unknown> | undefined>();

  // JSON editor modal
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [jsonContent, setJsonContent] = useState<Record<string, unknown>>({});
  const [jsonFormat, setJsonFormat] = useState<'tree' | 'json' | 'structured'>('tree');
  const [jsonEditValue, setJsonEditValue] = useState('');

  // Backend hook — replaces localStorage
  const {
    allTemplates,
    loading,
    saveTemplate,
    deleteTemplate,
    publishTemplate,
    refetch,
    isBackendAvailable,
  } = useTaskTemplates();

  const isArchitect = useHasRole('architect');

  // One-time localStorage migration: move old templates to backend
  useEffect(() => {
    const LEGACY_KEY = 'ferp:task-templates';
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw || !isBackendAvailable) return;

    try {
      const legacy = JSON.parse(raw) as Array<{
        name: string;
        description: string;
        category: string;
        tags: string[];
        state: TaskFormState;
      }>;

      if (legacy.length === 0) {
        localStorage.removeItem(LEGACY_KEY);
        return;
      }

      (async () => {
        let migrated = 0;
        for (const t of legacy) {
          const id = await saveTemplate(t.state, {
            name: t.name,
            description: t.description || '',
            category: t.category || 'general',
            tags: t.tags || [],
          });
          if (id) migrated++;
        }
        localStorage.removeItem(LEGACY_KEY);
        if (migrated > 0) {
          notifications.show({
            title: 'Templates Migrated',
            message: `${migrated} template${migrated > 1 ? 's' : ''} moved from browser storage to server.`,
            color: 'teal',
          });
        }
      })();
    } catch {
      // Corrupt localStorage — just remove it
      localStorage.removeItem(LEGACY_KEY);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBackendAvailable]);

  const rows = useMemo<TemplateRow[]>(
    () => allTemplates.map(templateToRow),
    [allTemplates],
  );

  const useTemplate = useCallback((row: TemplateRow) => {
    const t = row._source;
    if (t.formState) {
      setInitialData(serializeForInitialData(t.formState as TaskFormState) as Record<string, unknown>);
    }
    setFormKey(k => k + 1);
    setView('form');
  }, []);

  const viewJson = useCallback((row: TemplateRow) => {
    const t = row._source;
    const data = (t.formState ?? {}) as Record<string, unknown>;
    setJsonContent(data);
    setJsonEditValue(JSON.stringify(data, null, 2));
    setJsonModalOpen(true);
  }, []);

  const handleDelete = useCallback(async (row: TemplateRow) => {
    if (row.type === 'built-in') return;
    await deleteTemplate(row.id);
  }, [deleteTemplate]);

  const handlePublish = useCallback(async (row: TemplateRow) => {
    if (row.status !== 'Draft') return;
    await publishTemplate(row.id);
  }, [publishTemplate]);

  const handleClone = useCallback((state: TaskFormState) => {
    setInitialData(serializeForInitialData(state) as Record<string, unknown>);
    setFormKey(k => k + 1);
  }, []);

  const handleFormSuccess = useCallback(() => {
    setInitialData(undefined);
    setFormKey(k => k + 1);
  }, []);

  // Columns — using shared helpers from tabulatorConfig.ts
  const columns = useMemo<ColumnDefinition[]>(() => {
    const actionButtons = [
      { label: 'Use', cssClass: 'primary', className: 'tpl-use' },
      { label: '{ }', cssClass: 'secondary', className: 'tpl-json' },
      ...(isArchitect ? [{ label: 'Pub', cssClass: 'success', className: 'tpl-pub' }] : []),
      { label: 'X', cssClass: 'danger', className: 'tpl-del' },
    ];

    return [
      textColumn('name', { widthGrow: 2 }),
      typeBadgeColumn('type', { 'built-in': 'builtin', saved: 'saved' }, {
        headerFilter: 'list' as unknown as boolean,
        headerFilterParams: { values: { '': 'All', 'built-in': 'Built-in', 'saved': 'Saved' } },
      }),
      {
        title: 'Status',
        field: 'status',
        width: 120,
        formatter: (cell: any) => {
          const val = cell.getValue();
          const colorMap: Record<string, string> = {
            Published: '#40c057',
            Draft: '#fab005',
            PendingApproval: '#fd7e14',
            Archived: '#868e96',
          };
          const bg = colorMap[val] || '#868e96';
          return `<span style="display:inline-block;padding:2px 8px;border-radius:4px;background:${bg}20;color:${bg};font-size:11px;font-weight:600">${val}</span>`;
        },
        headerFilter: 'list' as unknown as boolean,
        headerFilterParams: { values: { '': 'All', 'Published': 'Published', 'Draft': 'Draft', 'PendingApproval': 'Pending' } },
      } as ColumnDefinition,
      textColumn('category', { width: 130, headerFilter: undefined }),
      textColumn('domain', { width: 130, headerFilter: undefined }),
      priorityColumn(),
      textColumn('tags', { widthGrow: 1, headerFilter: undefined }),
      timestampColumn('savedAt', { title: 'Created', width: 100 }),
      actionsColumn(
        actionButtons,
        (e, cell) => {
          const target = e.target as HTMLElement;
          // @ts-expect-error Type conversion
          const row = cell.getRow().getData() as TemplateRow;
          if (target.classList.contains('tpl-use')) useTemplate(row);
          else if (target.classList.contains('tpl-json')) viewJson(row);
          else if (target.classList.contains('tpl-pub')) handlePublish(row);
          else if (target.classList.contains('tpl-del')) handleDelete(row);
        },
      ),
    ];
  }, [useTemplate, viewJson, handleDelete, handlePublish, isArchitect]);

  // Initialize Tabulator with shared defaults
  useEffect(() => {
    if (!tableRef.current || view !== 'table') return;

    const instance = new Tabulator(tableRef.current, {
      ...TABULATOR_DEFAULTS,
      data: rows,
      columns,
      placeholder: 'No templates found',
    });

    tabulatorRef.current = instance;

    return () => {
      instance.destroy();
      tabulatorRef.current = null;
    };
  }, [rows, columns, view]);

  const handleJsonSave = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonEditValue);
      setJsonContent(parsed);
    } catch {
      // invalid JSON — keep modal open
    }
  }, [jsonEditValue]);

  const renderedContent = useMemo(() => {
    if (jsonFormat === 'structured') return toStructuredText(jsonContent);
    if (jsonFormat === 'json') return JSON.stringify(jsonContent, null, 2);
    return '';
  }, [jsonContent, jsonFormat]);

  // ============================================================================
  // TABLE VIEW
  // ============================================================================
  if (view === 'table') {
    return (
      <Container size="xl" py="md">
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="sm">
              <Title order={3}>Task Templates</Title>
              {!isBackendAvailable && (
                <Badge size="sm" color="yellow" variant="light">Offline — using local fallback</Badge>
              )}
            </Group>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => { setInitialData(undefined); setFormKey(k => k + 1); setView('form'); }}
            >
              Create New Task
            </Button>
          </Group>

          <Tabs defaultValue="templates">
            <Tabs.List>
              <Tabs.Tab value="templates" leftSection={<IconTemplate size={16} />}>
                Templates
              </Tabs.Tab>
              <Tabs.Tab value="task-groups" leftSection={<IconList size={16} />}>
                Task Groups
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="templates" pt="md">
              <Stack gap="md">
                <Text size="sm" c="dimmed">
                  Browse built-in and saved task templates. Click "Use" to clone into a new task, or "{'{ }'}" to inspect the full JSON.
                  {isArchitect && ' Click "Pub" to publish draft templates.'}
                </Text>
                {loading ? (
                  <Stack align="center" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">Loading templates from backend...</Text>
                  </Stack>
                ) : (
                  <div ref={tableRef} />
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="task-groups" pt="md">
              <TaskGroupBrowser />
            </Tabs.Panel>
          </Tabs>
        </Stack>

        {/* JSON Editor Modal */}
        <Modal
          opened={jsonModalOpen}
          onClose={() => setJsonModalOpen(false)}
          title={
            <Group gap="sm">
              <Text fw={600}>Template Inspector</Text>
              <SegmentedControl
                size="xs"
                value={jsonFormat}
                onChange={(v) => setJsonFormat(v as 'tree' | 'json' | 'structured')}
                data={[
                  { label: 'Tree', value: 'tree' },
                  { label: 'JSON', value: 'json' },
                  { label: 'Structured', value: 'structured' },
                ]}
              />
              {jsonFormat !== 'tree' && (
                <CopyButton value={renderedContent}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy'}>
                      <ActionIcon variant="subtle" color={copied ? 'teal' : 'gray'} size="sm" onClick={copy}>
                        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              )}
            </Group>
          }
          size="xl"
          padding="md"
        >
          {jsonFormat === 'tree' ? (
            <JsonTreeView data={jsonContent} />
          ) : jsonFormat === 'json' ? (
            <Stack gap="xs">
              <JsonInput
                value={jsonEditValue}
                onChange={setJsonEditValue}
                minRows={20}
                maxRows={30}
                autosize
                formatOnBlur
                validationError="Invalid JSON"
                styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
              />
              <Group justify="flex-end">
                <Button size="xs" variant="light" onClick={handleJsonSave}>
                  Apply Changes
                </Button>
                <Button
                  size="xs"
                  onClick={() => {
                    try {
                      const parsed = JSON.parse(jsonEditValue);
                      setInitialData(parsed);
                      setFormKey(k => k + 1);
                      setJsonModalOpen(false);
                      setView('form');
                    } catch { /* invalid JSON */ }
                  }}
                >
                  Use as Template
                </Button>
              </Group>
            </Stack>
          ) : (
            <ScrollArea h="calc(80vh - 120px)">
              <Code block style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {renderedContent}
              </Code>
            </ScrollArea>
          )}
        </Modal>
      </Container>
    );
  }

  // ============================================================================
  // FORM VIEW
  // ============================================================================
  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => setView('table')}
          >
            Back to Templates
          </Button>
        </Group>

        <ComprehensiveTaskForm
          key={formKey}
          mode="create"
          initialData={initialData as React.ComponentProps<typeof ComprehensiveTaskForm>['initialData']}
          templateToolbarEnabled
          onClone={handleClone}
          onSuccess={handleFormSuccess}
        />
      </Stack>
    </Container>
  );
}

export default TaskTemplatesPage;
